import React, { useEffect, useState, useCallback, useMemo } from 'react'
import classNames from 'classnames'
import { motion } from 'framer-motion'

type TimeRemaining = {
  total: number
  hours: number
  minutes: number
  seconds: number
}

export interface CountdownToMidnightProps {
  onCountdownEnd?: () => void
  className?: string
  numberClassName?: string
  textColorClass?: string
}

export const CountdownToMidnight: React.FC<CountdownToMidnightProps> = ({
  onCountdownEnd,
  className,
  numberClassName,
  textColorClass,
}) => {
  const formatNumber = (num: number): string => {
    return num < 10 ? `0${num}` : `${num}`
  }

  const getTimeRemainingUntilMidnightMST = useCallback((): TimeRemaining => {
    try {
      const now = new Date()
      const mstNow = new Date(now.toLocaleString('en-US', { timeZone: 'America/Denver' }))

      const midnight = new Date(mstNow)
      midnight.setHours(23, 59, 59, 999)

      const total = Math.max(0, midnight.getTime() - mstNow.getTime())

      return {
        total,
        hours: Math.floor((total / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((total / 1000 / 60) % 60),
        seconds: Math.floor((total / 1000) % 60),
      }
    } catch (error) {
      return { total: 0, hours: 0, minutes: 0, seconds: 0 }
    }
  }, [])

  const initialTimeRemaining = useMemo(() => getTimeRemainingUntilMidnightMST(), [getTimeRemainingUntilMidnightMST])

  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>(initialTimeRemaining)
  const [displayValues, setDisplayValues] = useState({
    hours: formatNumber(initialTimeRemaining.hours),
    minutes: formatNumber(initialTimeRemaining.minutes),
    seconds: formatNumber(initialTimeRemaining.seconds),
  })

  useEffect(() => {
    // Define timer update function
    const updateTimer = () => {
      const newTimeRemaining = getTimeRemainingUntilMidnightMST()
      setTimeRemaining(newTimeRemaining)

      setDisplayValues({
        hours: formatNumber(newTimeRemaining.hours),
        minutes: formatNumber(newTimeRemaining.minutes),
        seconds: formatNumber(newTimeRemaining.seconds),
      })

      if (newTimeRemaining.total <= 0 && onCountdownEnd) {
        onCountdownEnd()
      }
    }

    updateTimer()
    const timerId = setInterval(updateTimer, 1000)

    return () => clearInterval(timerId)
  }, [getTimeRemainingUntilMidnightMST, onCountdownEnd])

  if (timeRemaining.total <= 0) {
    return null
  }

  const timeUnitStyle =
    ' rounded-md bg-white/15 px-2  text-center backdrop-blur-sm flex items-center justify-center py-1'
  const separatorStyle = classNames('mx-0.5 text-sm font-normal ', textColorClass ? textColorClass : 'text-white')
  const timeUnitContainerStyle = 'mx-1 flex flex-col items-center'

  const pulseAnimation = {
    boxShadow: ['0 0 0 rgba(255,255,255,0)', '0 0 8px rgba(255,255,255,0.3)', '0 0 0 rgba(255,255,255,0)'],
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={classNames('flex flex-col items-center md:flex-row md:items-center ', className)}
    >
      <div className="flex items-center">
        {/* Hours */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className={timeUnitContainerStyle}
        >
          <div className={classNames(timeUnitStyle, numberClassName)}>
            <span className={classNames('font-bold', textColorClass ? textColorClass : 'text-white')}>
              {displayValues.hours}
            </span>
          </div>
        </motion.div>

        <span className={separatorStyle}>:</span>

        {/* Minutes */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className={timeUnitContainerStyle}
        >
          <div className={classNames(timeUnitStyle, numberClassName)}>
            <span className={classNames('font-bold', textColorClass ? textColorClass : 'text-white')}>
              {displayValues.minutes}
            </span>
          </div>
        </motion.div>

        <span className={separatorStyle}>:</span>

        {/* Seconds */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className={timeUnitContainerStyle}
        >
          <motion.div
            className={classNames(timeUnitStyle, numberClassName)}
            animate={pulseAnimation}
            transition={{
              repeat: Infinity,
              duration: 1.5,
              ease: 'easeInOut',
            }}
          >
            <span className={classNames('font-bold', textColorClass ? textColorClass : 'text-white')}>
              {displayValues.seconds}
            </span>
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
  )
}

export default CountdownToMidnight
